import React, { useEffect, useRef, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";
import { Comment, Like } from "../../assets/svg";
import { FullImageCard } from "./FullImageCard";
import { FullVideoCard } from "./FullVideoCard";
import { FullHtmlCard } from "./FullHtmlCard";
import { useAthletePostById } from "../../api/posts/queryHooks";
import { useParams } from "react-router-dom";
import { decodeId, get_media_url } from "../../api/utils";
import { IoIosArrowForward, IoIosArrowBack } from "react-icons/io";
import Topbanner, { getAppFunctionForStaticButtons } from "../../components/topbanner";
import db from "../../firebase/firebase";
import { FBCollectionConstant } from "../../firebase/consts";
import { collection, onSnapshot } from "firebase/firestore";

const DISABLE_STATES = {
  NONE: "",
  NEXT: "NEXT",
  PREV: "PREV",
};

const SlideMover = ({ swiperRef, handleNext, handlePrev }) => {
  const [whichBtnDisabled, setWhichBtnDisabled] = useState(DISABLE_STATES.NONE);

  useEffect(() => {
    if (!swiperRef.current) return;
    const swiper = swiperRef.current;
    const updateButtonStates = () => {
      setWhichBtnDisabled(
        swiper.isBeginning
          ? DISABLE_STATES.PREV
          : swiper.isEnd
          ? DISABLE_STATES.NEXT
          : DISABLE_STATES.NONE
      );
    };
    swiper.on("slideChange", updateButtonStates);
    swiper.on("init", updateButtonStates);
    updateButtonStates();
    return () => {
      swiper.off("slideChange", updateButtonStates);
      swiper.off("init", updateButtonStates);
    };
  }, [swiperRef]);

  return (
    <div className="absolute top-3 left-3 flex justify-center space-x-4 z-20">
      <button
        onClick={handlePrev}
        disabled={whichBtnDisabled === DISABLE_STATES.PREV}
        className="bg-[#2C2D36] rounded-full w-10 h-10 flex items-center justify-center hover:bg-[#272830] disabled:opacity-40 hover:opacity-100 transition-opacity duration-200"
      >
        <IoIosArrowBack />
      </button>
      <button
        onClick={handleNext}
        disabled={whichBtnDisabled === DISABLE_STATES.NEXT}
        className="bg-[#2C2D36] rounded-full w-10 h-10 flex items-center justify-center hover:bg-[#272830] disabled:opacity-40 hover:opacity-100 transition-opacity duration-200"
      >
        <IoIosArrowForward />
      </button>
    </div>
  );
};

const PostOptions = ({ postdetails }) => {
  const [likedCounts, setLikedCounts] = useState(0);
  const [commentsCount, setCommentsCount] = useState(0);
  const postId = postdetails.id;

  useEffect(() => {
    if (!postId) return;

    const commentCountUnsubscribe = onSnapshot(
      collection(
        db,
        FBCollectionConstant.userPosts,
        postId.toString(),
        FBCollectionConstant.comments
      ),
      (snapshot) => {
        setCommentsCount(snapshot.size);
      },
      (error) => {
        console.error("Error fetching comment count:", error);
      }
    );

    const likeCountUnsubscribe = onSnapshot(
      collection(
        db,
        FBCollectionConstant.userPosts,
        postId.toString(),
        FBCollectionConstant.likes
      ),
      (snapshot) => {
        setLikedCounts(snapshot.size);
      },
      (error) => {
        console.error("Error fetching like count:", error);
      }
    );
    return () => {
      commentCountUnsubscribe();
      likeCountUnsubscribe();
    };
  }, [postId]);

  return (
    <>
      <div className="absolute bottom-20 right-3 flex flex-col items-center gap-5 z-50 cursor-pointer">
        <div onClick={getAppFunctionForStaticButtons} className="text-[#FFFFFF] flex flex-col items-center">
          <Like />
          <span className="text-[12px]">{likedCounts}</span>
        </div>
        <div onClick={getAppFunctionForStaticButtons} className="text-[#FFFFFF] flex flex-col items-center">
          <Comment />
          <span className="text-[12px]">{commentsCount}</span>
        </div>
        {/* <div className="text-[#FFFFFF] flex flex-col items-center">
          <Post />
        </div> */}
        {/* <div className="text-[#FFFFFF] flex flex-col items-center">
          <SaveForLater />
        </div> */}
      </div>
    </>
  );
};

const PostDetails = ({ postdetails }) => {
  return (
    <>
      <div className="absolute bottom-3 left-3 z-50">
        <div onClick={getAppFunctionForStaticButtons} className="flex items-center gap-2 mb-2">
          <img
            alt="profileimage"
            src={get_media_url(postdetails.profile_dp)}
            className="w-6 h-6 rounded-full"
          />
          <p className="text-sm font-bold">{postdetails.owner_full_name}</p>
        </div>
        <div className="max-w-[200px]">
          <p className="text-[13px] truncate">{postdetails.description}</p>
        </div>
      </div>
      <div
        className="absolute bottom-0 left-0 bg-black h-[90px] w-full bg-gradient-to-t z-20 pointer-events-none"
        style={{
          background: "linear-gradient(to top, #00000099, #00000000)",
        }}
      ></div>
    </>
  );
};

const PostCardContainer = ({ data, postdetails }) => {
  const swiperRef = useRef(null);

  const handleNext = () => swiperRef.current && swiperRef.current.slideNext();
  const handlePrev = () => swiperRef.current && swiperRef.current.slidePrev();

  return (
    <>
      <Topbanner className={"mt-3"} showAtTop={false} />
      <div className="h-[calc(100vh-88px)] p-4 flex justify-center">
        <Swiper
          slidesPerView={1}
          className="post-container h-full aspect-[9/16] rounded-2xl relative bg-[#2C2D36]"
          onSwiper={(swiperInstance) => {
            swiperRef.current = swiperInstance;
          }}
        >
          {data.map((content, index) => {
            if (content?.content_type === "Image")
              return (
                <SwiperSlide key={index}>
                  <FullImageCard content={content} />
                </SwiperSlide>
              );
            else if (content?.content_type === "Video")
              return (
                <SwiperSlide key={index}>
                  <FullVideoCard content={content} />
                </SwiperSlide>
              );
            else if (content?.content_type === "HTML")
              return (
                <SwiperSlide key={index}>
                  <FullHtmlCard content={content} />
                </SwiperSlide>
              );
            else return null;
          })}
          <PostOptions postdetails={postdetails} />
          <PostDetails postdetails={postdetails} />
          {data?.length >= 2 ? (
            <SlideMover
              swiperRef={swiperRef}
              handleNext={handleNext}
              handlePrev={handlePrev}
            />
          ) : null}
        </Swiper>
      </div>
    </>
  );
};

function PostPage() {
  const id = decodeId(useParams()?.id);
  const { data, isLoading, isError } = useAthletePostById(id);

  if (isError) return <div>No Post Found!</div>;

  if (isLoading)
    return (
      <div className="w-screen h-screen p-4 flex justify-center">
        <div className="h-full aspect-[9/16] rounded-2xl skeleton"></div>
      </div>
    );

  if (data?.data?.postContent?.length)
    return (
      <PostCardContainer
        data={data.data.postContent}
        postdetails={{
          id: data?.data?.id,
          owner_username: data?.data?.owner_username || "",
          owner_full_name: data?.data?.owner_full_name || "",
          profile_dp: data?.data?.profile_dp || "",
          description: data?.data?.postContent?.[0]?.description || "",
        }}
      />
    );

  return <div>No Post Found!</div>;
}

export default PostPage;
